import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { FunctionDefinitionUpdate } from '../../core/api/models/function-definition.model';
import { FunctionQueueStatus, FunctionRun, FunctionRunLog } from '../../core/api/models/function-run.model';
import { PagedResult } from '../../core/api/models/pagination.model';
import { SyncFunctionSummary, SyncServiceSummary } from '../../core/api/models/sync-service.model';
import { UserImportChangeType, UserImportRecord } from '../../core/api/models/user-import.model';
import { FunctionRunsApiService } from '../../core/api/services/function-runs-api.service';
import { SyncServicesApiService } from '../../core/api/services/sync-services-api.service';
import { UserImportsApiService } from '../../core/api/services/user-imports-api.service';
import { localDateInputToUtcRange } from '../../core/date/local-day-range';
import {
  PaginationChange,
  PaginationComponent,
} from '../../shared/ui/pagination/pagination.component';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../shared/ui/pagination/pagination.config';
import { formatDurationMilliseconds, formatRunDuration } from '../../shared/ui/duration/duration';
import { TimeoutEditorComponent } from '../../shared/ui/timeout-editor/timeout-editor.component';
import { environment } from '../../../environments/environment';

type ServiceDetailTab = 'setup' | 'history' | 'accountability';

@Component({
  selector: 'app-sync-service-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PaginationComponent, TimeoutEditorComponent],
  template: `
    <section class="detail-page">
      <a class="back-link" routerLink="/sync-services"><i class="pi pi-arrow-left"></i> All sync services</a>
      <div class="message error" *ngIf="error"><span>{{error}}</span><button type="button" (click)="loadServices()">Try again</button></div>
      <div class="loading" *ngIf="loading && !service"><i class="pi pi-spin pi-spinner"></i><span>Loading service details…</span></div>

      <ng-container *ngIf="service as currentService">
        <header class="service-header">
          <div><p>Sync service</p><div class="title-line"><h2>{{currentService.source}}</h2><span class="status" [ngClass]="statusClass(currentService.latestRunStatus)">{{currentService.latestRunStatus || 'No runs'}}</span></div><span>{{currentService.enabledFunctions}} of {{currentService.totalFunctions}} functions enabled · {{currentService.importedUserCount}} retained user changes</span></div>
          <button type="button" (click)="loadServices()" [disabled]="loading"><i class="pi pi-refresh" [class.pi-spin]="loading"></i> Refresh</button>
        </header>

        <nav class="function-tabs" aria-label="Service functions">
          <a *ngFor="let item of currentService.functions" [routerLink]="['/sync-services',currentService.source,'functions',item.definition.name]" [class.active]="item.definition.name === selectedFunction?.definition?.name">
            <span>{{item.definition.functionType}}</span><strong>{{item.definition.name}}</strong><i class="state-dot" [class.enabled]="item.definition.isEnabled"></i>
          </a>
        </nav>

        <ng-container *ngIf="selectedFunction as selected">
          <section class="summary-grid">
            <article><span>Active jobs</span><strong>{{selected.activeRunCount}}</strong><small>Pending, running, retrying</small></article>
            <article><span>Successful runs</span><strong>{{selected.successfulRunCount}}</strong><small>Available history</small></article>
            <article><span>Failed runs</span><strong>{{selected.failedRunCount}}</strong><small>Final failures</small></article>
            <article><span>User changes</span><strong>{{selected.importedUserCount}}</strong><small>Retained audit records</small></article>
          </section>

          <div class="message success" *ngIf="actionMessage">{{actionMessage}}</div>
          <div class="message error" *ngIf="actionError">{{actionError}}</div>

          <nav class="detail-tabs" role="tablist" aria-label="Function detail sections">
            <button type="button" role="tab" id="setup-tab" [class.active]="activeTab === 'setup'" [attr.aria-selected]="activeTab === 'setup'" aria-controls="setup-panel" (click)="selectTab('setup')">
              <i class="pi pi-sliders-h" aria-hidden="true"></i><span><strong>Function setup</strong><small>Configuration and controls</small></span>
            </button>
            <button type="button" role="tab" id="history-tab" [class.active]="activeTab === 'history'" [attr.aria-selected]="activeTab === 'history'" aria-controls="history-panel" (click)="selectTab('history')">
              <i class="pi pi-history" aria-hidden="true"></i><span><strong>Execution history</strong><small>{{selected.successfulRunCount + selected.failedRunCount}} completed runs</small></span>
            </button>
            <button type="button" role="tab" id="accountability-tab"  [class.active]="activeTab === 'accountability'" [attr.aria-selected]="activeTab === 'accountability'" aria-controls="accountability-panel" (click)="selectTab('accountability')">
              <i class="pi pi-users" aria-hidden="true"></i><span><strong>User accountability</strong><small>{{selected.importedUserCount}} retained changes</small></span>
            </button>
          </nav>

          <section *ngIf="activeTab === 'setup'" id="setup-panel" class="configuration panel tab-panel wd-100" role="tabpanel" aria-labelledby="setup-tab">
            <div class="panel-heading"><div><p>Function setup</p><h3>Configuration</h3></div><div class="actions"><button *ngIf="!editing" type="button" class="secondary" (click)="startEditing()"><i class="pi pi-pencil"></i> Edit</button><button *ngIf="!editing" type="button" class="status-control" [class.enable]="!selected.definition.isEnabled" [disabled]="togglingEnabled || running" (click)="toggleEnabled()"><i [class]="togglingEnabled ? 'pi pi-spin pi-spinner' : (selected.definition.isEnabled ? 'pi pi-pause-circle' : 'pi pi-check-circle')"></i> {{selected.definition.isEnabled ? 'Disable' : 'Enable'}}</button><button *ngIf="!editing" type="button" class="primary" [disabled]="running || !selected.definition.isEnabled || togglingEnabled" (click)="runNow()"><i [class]="running ? 'pi pi-spin pi-spinner' : 'pi pi-play'"></i> {{running ? 'Queuing…' : 'Run now — high priority'}}</button></div></div>
            <dl *ngIf="!editing"><div><dt>Function</dt><dd>{{selected.definition.name}}</dd></div><div><dt>Type</dt><dd>{{selected.definition.functionType}}</dd></div><div><dt>Schedule mode</dt><dd>{{selected.definition.scheduleMode}}</dd></div><div><dt>Status</dt><dd>{{selected.definition.isEnabled ? 'Enabled' : 'Disabled'}}</dd></div><div><dt>Cron expression</dt><dd>{{selected.definition.cronExpression || '—'}}</dd></div><div><dt>Priority</dt><dd>{{selected.definition.priority}}</dd></div><div><dt>Maximum retries</dt><dd>{{selected.definition.maxRetryCount}}</dd></div><div><dt>Timeout</dt><dd>{{timeoutLabel(selected.definition.timeoutMilliseconds)}}</dd></div><div><dt>Last run</dt><dd>{{selected.definition.lastRunAtUtc ? (selected.definition.lastRunAtUtc|date:'medium') : 'Never'}}</dd></div><div><dt>Next run</dt><dd>{{selected.definition.nextRunAtUtc ? (selected.definition.nextRunAtUtc|date:'medium') : 'Not scheduled'}}</dd></div><div class="wide"><dt>Endpoint URL</dt><dd>{{selected.definition.url}}</dd></div></dl>
            <form *ngIf="editing" (ngSubmit)="saveConfiguration()" class="edit-form">
              <label>Schedule mode<select [(ngModel)]="draft.scheduleMode" name="scheduleMode"><option value="Scheduled">Scheduled</option><option value="ManualOnly">Manual only</option></select></label>
              <label>Cron expression<input [(ngModel)]="draft.cronExpression" name="cronExpression" [disabled]="draft.scheduleMode === 'ManualOnly'"></label>
              <label>Priority<input type="number" min="1" max="5" [(ngModel)]="draft.priority" name="priority" required></label>
              <label>Maximum retries<input type="number" min="0" [(ngModel)]="draft.maxRetryCount" name="maxRetryCount" required></label>
              <label>Timeout<app-timeout-editor [(timeoutMilliseconds)]="draft.timeoutMilliseconds" [disabled]="saving" /></label>
              <label class="wide">Endpoint URL<input type="url" [(ngModel)]="draft.url" name="url" required></label>
              <div class="form-actions wide"><button type="button" class="secondary" (click)="editing=false">Cancel</button><button type="submit" class="primary" [disabled]="saving">{{saving ? 'Saving…' : 'Save configuration'}}</button></div>
            </form>
          </section>

          <section *ngIf="activeTab === 'history'" id="history-panel"  class="panel tab-panel wd-100" role="tabpanel" aria-labelledby="history-tab">
            <div class="panel-heading"><div><p>Execution history</p><h3>Queue and completed runs</h3></div><a routerLink="/runs">Open all runs</a></div>
            <article class="run-log" *ngIf="selectedRun as monitoredRun" [attr.aria-busy]="runLogLoading">
              <header><div><p>Execution log</p><div class="run-log-title"><h4>{{monitoredRun.functionName}}</h4><span class="status" [ngClass]="statusClass(monitoredRun.status)">{{monitoredRun.status}}</span></div><small>{{monitoredRun.triggerSource}} trigger · Attempt {{monitoredRun.attemptCount}} · {{monitoredRun.maxRetryCount}} max retries</small></div><div class="actions"><button type="button" class="secondary" [disabled]="runLogLoading" (click)="loadRunLogs()"><i class="pi pi-refresh" [class.pi-spin]="runLogLoading"></i> Refresh</button><button type="button" class="icon-action" aria-label="Close execution log" (click)="closeRunLogs()"><i class="pi pi-times"></i></button></div></header>
              <p class="inline-error" *ngIf="monitoredRun.failureReason"><strong>Failure reason:</strong> {{monitoredRun.failureReason}}</p>
              <p class="inline-error" *ngIf="runLogError">{{runLogError}}</p>
              <ol class="log-stream" *ngIf="runLogs.length; else noRunLogs">
                <li *ngFor="let log of runLogs" [class.warning]="log.level === 'Warning'" [class.error]="log.level === 'Error'"><time>{{log.occurredAtUtc | date:'medium'}}</time><span class="log-level">{{log.level}}</span><div><strong>{{log.eventType}}</strong><p>{{log.message}}</p></div></li>
              </ol>
              <ng-template #noRunLogs><p class="log-empty">{{runLogLoading ? 'Loading execution logs…' : 'No execution events have been recorded yet.'}}</p></ng-template>
            </article>
            <div class="filters">
              <label>Status<select [(ngModel)]="historyStatus"><option value="">All statuses</option><option *ngFor="let status of statuses" [ngValue]="status">{{status}}</option></select></label>
              <label>From<input type="date" [(ngModel)]="historyFromDate"></label><label>To<input type="date" [(ngModel)]="historyToDate"></label>
              <button type="button" class="primary" (click)="applyHistoryFilters()">Apply</button><button type="button" class="secondary" (click)="clearHistoryFilters()">Clear</button>
            </div>
            <p class="inline-error" *ngIf="historyError">{{historyError}}</p>
            <div class="table-wrap" *ngIf="history?.items?.length; else noHistory"><table><thead><tr><th>Status</th><th>Trigger</th><th>Queued</th><th>Completed</th><th>Duration</th><th>Attempts</th><th>Logs</th></tr></thead><tbody><tr *ngFor="let run of history?.items"><td><a [routerLink]="['/runs',run.id]"><span class="status" [ngClass]="statusClass(run.status)">{{run.status}}</span></a></td><td>{{run.triggerSource}}</td><td>{{run.queuedAtUtc|date:'medium'}}</td><td>{{run.completedAtUtc ? (run.completedAtUtc|date:'medium') : '—'}}</td><td>{{duration(run)}}</td><td>{{run.attemptCount}}</td><td><button type="button" class="log-link" (click)="openRunLogs(run)"><i class="pi pi-file"></i> View logs</button></td></tr></tbody></table></div>
            <ng-template #noHistory><div class="empty"><strong>{{historyLoading ? 'Loading history…' : 'No matching runs'}}</strong><span *ngIf="!historyLoading">Change the filters or run this function.</span></div></ng-template>
            <app-pagination *ngIf="history" [page]="historyPaging.page" [pageSize]="historyPaging.pageSize" [totalCount]="history.totalCount" [loading]="historyLoading" itemLabel="runs" (paginationChange)="changeHistoryPagination($event)" />
          </section>

          <section *ngIf="activeTab === 'accountability'" id="accountability-panel" class="panel tab-panel wd-100" role="tabpanel" aria-labelledby="accountability-tab">
            <div class="panel-heading"><div><p>User accountability</p><h3>Imported users</h3></div><a routerLink="/user-imports">Open all imports</a></div>
            <div class="filters import-filters">
              <label>Change type<select [(ngModel)]="importChangeType"><option value="">All changes</option><option *ngFor="let type of changeTypes" [ngValue]="type">{{type}}</option></select></label>
              <label>Email contains<input type="search" [(ngModel)]="importEmail" placeholder="name@example.com"></label>
              <label>From<input type="date" [(ngModel)]="importFromDate"></label><label>To<input type="date" [(ngModel)]="importToDate"></label>
              <button type="button" class="primary" (click)="applyImportFilters()">Apply</button><button type="button" class="secondary" (click)="clearImportFilters()">Clear</button>
            </div>
            <p class="inline-error" *ngIf="importsError">{{importsError}}</p>
            <div class="table-wrap" *ngIf="imports?.items?.length; else noImports"><table><thead><tr><th>Email</th><th>Source</th><th>Change</th><th>Created</th></tr></thead><tbody><tr *ngFor="let item of imports?.items"><td><a [routerLink]="['/user-imports',item.publicId]"><strong>{{item.email || 'User record'}}</strong></a></td><td>{{item.source}}</td><td><span class="change" [class]="'change '+item.changeType.toLowerCase()">{{item.changeType}}</span></td><td>{{item.createdAtUtc|date:'medium'}}</td></tr></tbody></table></div>
            <ng-template #noImports><div class="empty"><strong>{{importsLoading ? 'Loading imported users…' : 'No matching user changes'}}</strong><span *ngIf="!importsLoading">Change the filters or check another function.</span></div></ng-template>
            <app-pagination *ngIf="imports" [page]="importsPaging.page" [pageSize]="importsPaging.pageSize" [totalCount]="imports.totalCount" [loading]="importsLoading" itemLabel="imports" (paginationChange)="changeImportsPagination($event)" />
          </section>
        </ng-container>
      </ng-container>

      <div class="not-found" *ngIf="notFound"><i class="pi pi-exclamation-circle"></i><h2>Service or function not found</h2><p>The requested source/function combination is not configured.</p><a routerLink="/sync-services">Return to Sync Services</a></div>
    </section>
  `,
  styleUrl: './sync-service-detail.component.scss',
})
export class SyncServiceDetailComponent implements OnInit {
  private readonly syncServicesApi = inject(SyncServicesApiService);
  private readonly functionRunsApi = inject(FunctionRunsApiService);
  private readonly userImportsApi = inject(UserImportsApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly statuses: FunctionQueueStatus[] = ['Pending', 'Running', 'Cancelling', 'Retrying', 'Successful', 'Failed', 'Cancelled'];
  readonly changeTypes: UserImportChangeType[] = ['Created', 'Updated', 'Deleted'];

  services: SyncServiceSummary[] = [];
  service: SyncServiceSummary | null = null;
  selectedFunction: SyncFunctionSummary | null = null;
  history: PagedResult<FunctionRun> | null = null;
  imports: PagedResult<UserImportRecord> | null = null;
  loading = false;
  historyLoading = false;
  importsLoading = false;
  saving = false;
  running = false;
  togglingEnabled = false;
  editing = false;
  notFound = false;
  error = '';
  historyError = '';
  importsError = '';
  actionError = '';
  actionMessage = '';
  activeTab: ServiceDetailTab = 'setup';
  selectedRun: FunctionRun | null = null;
  runLogs: FunctionRunLog[] = [];
  runLogLoading = false;
  runLogError = '';

  historyPaging: PaginationChange = { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE };
  importsPaging: PaginationChange = { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE };
  historyStatus: FunctionQueueStatus | '' = '';
  historyFromDate = '';
  historyToDate = '';
  importChangeType: UserImportChangeType | '' = '';
  importEmail = '';
  importFromDate = '';
  importToDate = '';
  draft: FunctionDefinitionUpdate = this.emptyDraft();

  private requestedSource = '';
  private requestedFunctionName = '';
  private runLogTimer?: number;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.stopRunLogPolling());
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((parameters) => {
      this.requestedSource = parameters.get('source') ?? '';
      this.requestedFunctionName = parameters.get('functionName') ?? '';
      if (this.services.length > 0) {
        this.resolveRoute();
      } else {
        this.loadServices();
      }
    });
  }

  loadServices(): void {
    this.loading = true;
    this.error = '';
    const request = this.syncServicesApi.getSyncServices();
    request.pipe(finalize(() => this.loading = false)).subscribe({
      next: (result) => {
        this.services = result;
        this.resolveRoute();
      },
      error: () => this.error = 'Service details could not be loaded.',
    });
  }

  startEditing(): void {
    if (!this.selectedFunction) {
      return;
    }
    this.draft = this.createDraft(this.selectedFunction);
    this.editing = true;
    this.actionError = '';
    this.actionMessage = '';
  }

  saveConfiguration(): void {
    const functionName = this.selectedFunction?.definition.name;
    if (!functionName) {
      return;
    }
    if (!this.draft.url.trim()) {
      this.actionError = 'Endpoint URL is required.';
      return;
    }

    const update: FunctionDefinitionUpdate = {
      ...this.draft,
      url: this.draft.url.trim(),
      cronExpression: this.draft.scheduleMode === 'ManualOnly' ? null : this.draft.cronExpression?.trim(),
    };
    this.saving = true;
    this.actionError = '';
    const request = this.syncServicesApi.updateDefinition(functionName, update);
    request.pipe(finalize(() => this.saving = false)).subscribe({
      next: () => {
        this.editing = false;
        this.actionMessage = 'Configuration saved.';
        this.loadServices();
      },
      error: (response) => this.actionError = response.error?.message || 'Configuration could not be saved.',
    });
  }

  runNow(): void {
    const definition = this.selectedFunction?.definition;
    if (!definition || !definition.isEnabled) {
      return;
    }
    this.running = true;
    this.actionError = '';
    this.actionMessage = '';
    const request = this.syncServicesApi.runDefinition(definition.name);
    request.pipe(finalize(() => this.running = false)).subscribe({
      next: (run) => {
        this.actionMessage = 'High-priority manual run was queued.';
        this.activeTab = 'history';
        this.openRunLogs(run);
        this.loadHistory();
      },
      error: (response) => {
        const activeRun = response.error?.run as FunctionRun | undefined;
        if (response.status === 409 && activeRun) {
          this.actionError = 'The manual run was not queued because this function already has an active run. Wait for it to finish, then try again.';
          this.activeTab = 'history';
          this.openRunLogs(activeRun);
          return;
        }
        this.actionError = response.error?.message || 'The run could not be queued.';
      },
    });
  }

  toggleEnabled(): void {
    const definition = this.selectedFunction?.definition;
    if (!definition || this.togglingEnabled) return;

    const isEnabled = !definition.isEnabled;
    this.togglingEnabled = true;
    this.actionError = '';
    this.actionMessage = '';
    this.syncServicesApi.setDefinitionEnabled(definition.name, isEnabled)
      .pipe(finalize(() => this.togglingEnabled = false))
      .subscribe({
        next: () => {
          this.actionMessage = isEnabled
            ? 'Function enabled.'
            : 'Function disabled. Pending and retrying runs were cancelled.';
          this.loadServices();
        },
        error: (response) => this.actionError = response.error?.message
          || 'The function status could not be changed.',
      });
  }

  openRunLogs(run: FunctionRun): void {
    this.stopRunLogPolling();
    this.selectedRun = run;
    this.runLogs = [];
    this.runLogError = '';
    this.loadRunLogs();
    if (this.isActiveStatus(run.status)) {
      this.runLogTimer = window.setInterval(
        () => this.loadRunLogs(),
        environment.refreshIntervals.runLogsMilliseconds);
    }
  }

  closeRunLogs(): void {
    this.stopRunLogPolling();
    this.selectedRun = null;
    this.runLogs = [];
    this.runLogError = '';
  }

  loadRunLogs(): void {
    const runId = this.selectedRun?.id;
    if (!runId || this.runLogLoading) return;
    this.runLogLoading = true;
    this.runLogError = '';
    this.functionRunsApi.getRun(runId).subscribe({
      next: (run) => {
        if (this.selectedRun?.id !== runId) return;
        this.selectedRun = run;
        if (!this.isActiveStatus(run.status)) {
          this.stopRunLogPolling();
          this.loadHistory();
        }
      },
      error: () => this.runLogError = 'Run status could not be refreshed.',
    });
    this.functionRunsApi.getRunLogs(runId).pipe(finalize(() => this.runLogLoading = false)).subscribe({
      next: (logs) => {
        if (this.selectedRun?.id === runId) this.runLogs = logs;
      },
      error: () => this.runLogError = 'Execution logs could not be loaded.',
    });
  }

  selectTab(tab: ServiceDetailTab): void {
    this.activeTab = tab;
  }

  applyHistoryFilters(): void {
    this.historyPaging = { ...this.historyPaging, page: DEFAULT_PAGE };
    this.loadHistory();
  }

  clearHistoryFilters(): void {
    this.historyStatus = '';
    this.historyFromDate = '';
    this.historyToDate = '';
    this.applyHistoryFilters();
  }

  changeHistoryPagination(paging: PaginationChange): void {
    this.historyPaging = paging;
    this.loadHistory();
  }

  applyImportFilters(): void {
    this.importsPaging = { ...this.importsPaging, page: DEFAULT_PAGE };
    this.loadImports();
  }

  clearImportFilters(): void {
    this.importChangeType = '';
    this.importEmail = '';
    this.importFromDate = '';
    this.importToDate = '';
    this.applyImportFilters();
  }

  changeImportsPagination(paging: PaginationChange): void {
    this.importsPaging = paging;
    this.loadImports();
  }

  statusClass(status?: FunctionQueueStatus | null): string {
    const result = `status-${(status ?? 'unknown').toLowerCase()}`;
    return result;
  }

  timeoutLabel(timeoutMilliseconds: number): string {
    return formatDurationMilliseconds(timeoutMilliseconds);
  }

  duration(run: FunctionRun): string {
    return formatRunDuration(run.startedAtUtc, run.completedAtUtc);
  }

  private resolveRoute(): void {
    const service = this.services.find((item) =>
      item.source.toLowerCase() === this.requestedSource.toLowerCase());
    if (!service) {
      this.service = null;
      this.selectedFunction = null;
      this.notFound = true;
      return;
    }

    this.service = service;
    this.notFound = false;
    if (!this.requestedFunctionName && service.functions.length > 0) {
      const firstFunctionName = service.functions[0].definition.name;
      const navigation = this.router.navigate(
        ['/sync-services', service.source, 'functions', firstFunctionName],
        { replaceUrl: true });
      void navigation;
      return;
    }

    const selected = service.functions.find((item) =>
      item.definition.name.toLowerCase() === this.requestedFunctionName.toLowerCase());
    if (!selected) {
      this.selectedFunction = null;
      this.notFound = true;
      return;
    }

    const selectionChanged = this.selectedFunction?.definition.name !== selected.definition.name;
    this.selectedFunction = selected;
    this.draft = this.createDraft(selected);
    if (selectionChanged) {
      this.editing = false;
      this.actionError = '';
      this.actionMessage = '';
      this.closeRunLogs();
      this.historyPaging = { ...this.historyPaging, page: DEFAULT_PAGE };
      this.importsPaging = { ...this.importsPaging, page: DEFAULT_PAGE };
    }
    this.loadHistory();
    this.loadImports();
  }

  private loadHistory(): void {
    const functionName = this.selectedFunction?.definition.name;
    if (!functionName) {
      return;
    }
    const dateRange = localDateInputToUtcRange(this.historyFromDate, this.historyToDate);
    this.historyLoading = true;
    this.historyError = '';
    const request = this.functionRunsApi.getRuns(this.historyPaging.page, this.historyPaging.pageSize, {
      functionName,
      status: this.historyStatus || undefined,
      fromUtc: dateRange.fromUtc,
      toUtc: dateRange.toUtc,
    });
    request.pipe(finalize(() => this.historyLoading = false)).subscribe({
      next: (result) => {
        this.history = result;
        this.historyPaging = { page: result.page, pageSize: result.pageSize };
      },
      error: (response) => this.historyError = this.validationMessage(response, 'Run history could not be loaded.'),
    });
  }

  private loadImports(): void {
    const functionName = this.selectedFunction?.definition.name;
    if (!functionName) {
      return;
    }
    const dateRange = localDateInputToUtcRange(this.importFromDate, this.importToDate);
    this.importsLoading = true;
    this.importsError = '';
    const request = this.userImportsApi.getUserImports(this.importsPaging.page, this.importsPaging.pageSize, {
      functionName,
      email: this.importEmail.trim() || undefined,
      changeType: this.importChangeType || undefined,
      fromUtc: dateRange.fromUtc,
      toUtc: dateRange.toUtc,
    });
    request.pipe(finalize(() => this.importsLoading = false)).subscribe({
      next: (result) => {
        this.imports = result;
        this.importsPaging = { page: result.page, pageSize: result.pageSize };
      },
      error: (response) => this.importsError = this.validationMessage(response, 'Imported users could not be loaded.'),
    });
  }

  private createDraft(summary: SyncFunctionSummary): FunctionDefinitionUpdate {
    const definition = summary.definition;
    const result: FunctionDefinitionUpdate = {
      scheduleMode: definition.scheduleMode,
      cronExpression: definition.cronExpression,
      priority: definition.priority,
      maxRetryCount: definition.maxRetryCount,
      timeoutMilliseconds: definition.timeoutMilliseconds,
      url: definition.url,
    };
    return result;
  }

  private emptyDraft(): FunctionDefinitionUpdate {
    const result: FunctionDefinitionUpdate = {
      scheduleMode: 'Scheduled',
      cronExpression: '',
      priority: 3,
      maxRetryCount: 0,
      timeoutMilliseconds: 300000,
      url: '',
    };
    return result;
  }

  private validationMessage(response: any, fallback: string): string {
    const errors = response?.error?.errors as Record<string, string[]> | undefined;
    const firstMessage = errors ? Object.values(errors).flat()[0] : undefined;
    const result = firstMessage || response?.error?.message || response?.error?.title || fallback;
    return result;
  }

  private isActiveStatus(status: FunctionQueueStatus): boolean {
    return status === 'Pending' || status === 'Running' || status === 'Retrying';
  }

  private stopRunLogPolling(): void {
    if (this.runLogTimer !== undefined) {
      window.clearInterval(this.runLogTimer);
      this.runLogTimer = undefined;
    }
  }
}
